import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Eye, EyeOff } from 'lucide-react';
import { referralPartnerApi } from '@/api/referralPartner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';

export default function PartnerLogin() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await referralPartnerApi.login(email, password);
      const payload = res.data?.data;
      localStorage.setItem('partner_token', payload.token);
      localStorage.setItem('partner_user', JSON.stringify(payload.partner));
      toast.success('Welcome back');
      navigate('/partner');
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      toast.error(msg || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-[#0a0f1a] px-4 py-10 sm:py-0">
      <div
        className="pointer-events-none fixed inset-0 opacity-50"
        style={{
          background:
            'radial-gradient(ellipse 70% 50% at 50% 0%, rgba(34,211,238,0.25), transparent)',
        }}
      />
      <div className="relative w-full max-w-md rounded-3xl border border-white/10 bg-slate-900/80 p-5 sm:p-8 shadow-2xl backdrop-blur-xl">
        <p className="text-center text-xs font-semibold uppercase tracking-widest text-cyan-400">
          Partner Portal
        </p>
        <h1 className="mt-2 text-center text-2xl font-bold text-white">Sign in</h1>
        <p className="mt-1 text-center text-sm text-slate-400">
          Refer patients and track your rewards
        </p>
        <form onSubmit={submit} className="mt-8 space-y-4">
          <Input
            type="email"
            placeholder="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="border-white/10 bg-white/5 text-white placeholder:text-slate-500 h-11"
            autoComplete="email"
            required
          />
          <div>
            <div className="relative">
              <Input
                type={showPassword ? 'text' : 'password'}
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="border-white/10 bg-white/5 text-white placeholder:text-slate-500 h-11 pr-10"
                autoComplete="current-password"
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                className="absolute inset-y-0 right-0 flex items-center pr-3 text-slate-400 hover:text-white"
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
              </button>
            </div>
            <div className="mt-2 flex justify-end">
              <Link
                to="/partner/forgot-password"
                state={{ email }}
                className="text-sm font-medium text-cyan-400 hover:underline"
              >
                Forgot password?
              </Link>
            </div>
          </div>
          <Button
            type="submit"
            disabled={loading}
            className="w-full h-11 bg-gradient-to-r from-cyan-500 to-violet-600 hover:from-cyan-400 hover:to-violet-500"
          >
            {loading ? 'Signing in…' : 'Sign in'}
          </Button>
        </form>
        <p className="mt-6 text-center text-sm text-slate-400">
          New partner?{' '}
          <Link to="/partner/register" className="font-medium text-cyan-400 hover:underline">
            Register
          </Link>
        </p>
      </div>
    </div>
  );
}
